import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IGroupEntityDTO } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { Observable, Subject, distinctUntilChanged } from 'rxjs';

import { TemplatesProvider } from '../../../provider/templates.provider';
import { BaseComponent } from '../../base.component';
import { GroupFormComponent } from '../group-form/group-form.component';

const debug = Debug('aumico:aumico-lib-templates-fe:GroupListComponent');

@UntilDestroy()
@Component({
    selector: 'templates-group-list',
    templateUrl: 'group-list.component.html',
    styleUrls: ['group-list.component.scss'],
})
export class GroupListComponent extends BaseComponent implements OnInit {
    @Input() currentLanguage = 'de-ch';
    @Input() public set injectedProviderSetter(injectedProvider: TemplatesProvider) {
        if (injectedProvider) {
            this.templatesProvider = injectedProvider;
        }
    }

    public groups$: Observable<IGroupEntityDTO[]>;
    public groups: IGroupEntityDTO[] = [];
    public activeGroup: IGroupEntityDTO;
    public editGroupClicked = new Subject<string>();
    public updateGroup = new Subject<IGroupEntityDTO>();
    public createGroup = new Subject<IGroupEntityDTO>();
    public editGroupFormClosed = new Subject<boolean>();
    public deleteGroupClicked = new Subject<string>();
    public checkIfGroupNameExists = new Subject<IGroupEntityDTO>();

    constructor(
        public override templatesProvider: TemplatesProvider,
        @Inject(MAT_DIALOG_DATA) private data: { groupNameExistsError: Subject<string> },
        public dialogRef: MatDialogRef<GroupListComponent>,
        private dialog: MatDialog,
    ) {
        super(templatesProvider);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.groups$
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((groups: IGroupEntityDTO[]) => (this.groups = groups));
    }

    addOrUpdateGroup(groupId?: string) {
        if (groupId) {
            this.editGroupClicked.next(groupId);
        }
        const dialogRef = this.dialog.open(GroupFormComponent, {
            width: '385px',
            disableClose: true,
            data: { activeGroup: this.activeGroup, groupNameExistsError: this.data.groupNameExistsError },
        });
        dialogRef.componentInstance.injectedProviderSetter = this.templatesProvider;
        dialogRef.componentInstance.savePressed.subscribe((group) => {
            this.checkIfGroupNameExists.next(group);
        });
        dialogRef.componentInstance.updateGroup.subscribe((group) => {
            this.updateGroup.next(group);
        });

        dialogRef.componentInstance.createGroup.subscribe((group) => {
            this.createGroup.next(group);
        });
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe(() => {
                this.editGroupFormClosed.next(true);
                dialogRef.componentInstance.createGroup.unsubscribe();
            }),
        );
    }

    removeGroup(groupId: string) {
        this.deleteGroupClicked.next(groupId);
    }

    save() {
        let selectedGroups: IGroupEntityDTO[] = this.groups.filter((group) => group.selected);
        selectedGroups = selectedGroups.map((group) => {
            delete group.selected;
            return group;
        });
        this.closeDialog(selectedGroups);
    }

    closeDialog(data?: IGroupEntityDTO[]) {
        if (typeof this.dialogRef === 'object' && !(Object.keys(this.dialogRef).length === 0)) {
            this.dialogRef.close(data);
        }
    }
}
